import type { Drip, DripStep } from '@bt-common/schemas/bt/drip';
import type { DripTemplate } from '@common/models/drip-template';

/**
 * FE#331 — the two pure transforms behind "save a drip as a template" and
 * "apply a template to spin up a new drip". Kept as named, tested functions
 * because each has a guard that, if wrong, either loses the sequence or (worse)
 * launches something it shouldn't.
 */

/** Deep-ish clone of the steps array so the template never shares object
 *  identity with the source drip's live steps (a later drip edit must not
 *  mutate a saved template, and vice-versa). Steps are plain JSON (message
 *  body + gap), so structuredClone-free JSON round-trip is sufficient and
 *  avoids pulling in a clone dep. */
const cloneSteps = (steps: DripStep[] | undefined): DripStep[] =>
  (steps ?? []).map((s) => JSON.parse(JSON.stringify(s)) as DripStep);

/**
 * Build the `drip-templates` create payload from an existing drip.
 *
 * Captures ONLY the reusable definition — the ordered `steps` (each carrying
 * its message body AND its `gapAfter` offset) plus `stopOnReply`. It never
 * copies the audience (`boardIDArray`), the schedule, or any runtime state
 * (`status`/`currentStep`/`exitedRecipients`/timestamps): those are bound fresh
 * when the template is applied. The payload also carries NO `_id` — this is
 * always a create.
 */
export const buildDripTemplatePayload = (
  drip: Pick<Drip, 'steps' | 'stopOnReply'>,
  name: string,
): Partial<DripTemplate> => ({
  name: name.trim(),
  steps: cloneSteps(drip.steps),
  stopOnReply: drip.stopOnReply ?? false,
});

/**
 * Seed a fresh DRAFT drip from a saved template.
 *
 * Mirrors the empty-draft shape `campaign-draft-creator` persists for
 * `/campaigns/new` — `status:'draft'`, empty audience, non-recurring
 * schedule — but pre-populated with the template's `steps` (and name +
 * `stopOnReply`). The user then picks the target group/audience + start date in
 * the existing wizard. Carries NO `_id` (always a create) and NO runtime state.
 */
export const seedDraftDripFromTemplate = (
  template: Pick<DripTemplate, 'name' | 'steps' | 'stopOnReply'>,
): Partial<Drip> => ({
  name: template.name ?? '',
  status: 'draft',
  boardIDArray: [],
  steps: cloneSteps(template.steps),
  // Same default as a blank drip: a group recipient exits on any member's post,
  // so exit-on-reply is off unless the saved template explicitly enabled it.
  stopOnReply: template.stopOnReply ?? false,
  currentStep: 0,
  schedule: { isRecurring: false, isTimeValid: false },
});
